import React from 'react';
import useResizeObserver from '@packages/systems/ui/hooks/useResizeObserver';
import {
  ExpressionPath,
  Expressions,
  TypeImplementation,
  Types,
  useDangerousHooks,
  withSystem,
} from '@packages/systems/wfdl';
import {
  TRecord,
  TUnion,
  TEmptyRecord,
  TNumber,
} from '@packages/systems/wfdl-ast';
import {
  BREAKPOINT_ID_MAIN,
  DEFAULT_BREAKPOINT_IDS,
  LARGER_BREAKPOINT_IDS,
} from '@packages/systems/style/breakpoint-ids';
import {LARGER_BREAKPOINTS_CONFIG} from '@packages/systems/style/breakpoints-config';
import {type BreakpointID} from '@packages/systems/style/types';
import {
  ContextualExpression,
  ContextualExpressions,
  stripAll,
} from '@packages/utilities/contextual-expression';
import {
  DesignerStores,
  useDesignerSelector,
} from '@packages/systems/designer/flux';
import {
  FRAME_DIMENSIONS_CHANGED,
  FRAME_DIMENSION_OVERRIDE_RESET,
} from '@packages/systems/designer/actions';
import {
  FrameWidth,
  FrameHeight,
} from '@packages/systems/component-canvas/types';
import {
  FRAME_DIMENSION_MODE,
  getBaseVariantFrame,
  getFrameById,
  getMinMaxFrameWidthForBreakpoint,
  getMinMaxFrameHeight,
  getOverrides,
  getVariantAssociatedWithFrame,
  getVariantInstanceElementFromFrame,
  selectGetBaseVariantId,
  selectSelectedFrameElementId,
} from '@packages/systems/component-canvas/designer';

import {HStack, Select, VStack} from '@packages/systems/spring';
import {selectComponents} from '@packages/utilities/platform-selectors';
import {FrameFieldLabel, findInheritedFrom} from './FrameFieldLabel';
import {
  useFrameSizeTransition,
  FrameSizeUnitInput,
} from '@packages/systems/open-canvas/ui';

const createFrameDimensionPath = (
  mediaQuery: string,
  dimension?: 'width' | 'height',
  field?: 'mode' | 'value'
): ExpressionPath => {
  const path: ExpressionPath = [{in: 'Record', at: mediaQuery}];

  if (dimension) {
    path.push({in: 'Record', at: dimension});
  }

  if (field) {
    path.push({in: 'Record', at: field});
  }

  return path;
};

const FrameWidthMode = TUnion({
  [FRAME_DIMENSION_MODE.FIT]: TEmptyRecord,
  [FRAME_DIMENSION_MODE.FULL]: TEmptyRecord,
  [FRAME_DIMENSION_MODE.FIXED]: TEmptyRecord,
} as const satisfies Record<FrameWidth['mode'], typeof TEmptyRecord>);

const FrameHeightMode = TUnion({
  [FRAME_DIMENSION_MODE.FIT]: TEmptyRecord,
  [FRAME_DIMENSION_MODE.FIXED]: TEmptyRecord,
} as const satisfies Record<FrameHeight['mode'], typeof TEmptyRecord>);

const FrameWidthRecord = TRecord({
  mode: withSystem(
    {defaultValue: {tag: FRAME_DIMENSION_MODE.FULL}},
    FrameWidthMode
  ),
  value: TNumber,
});

const FrameHeightRecord = TRecord({
  mode: withSystem(
    {defaultValue: {tag: FRAME_DIMENSION_MODE.FIT}},
    FrameHeightMode
  ),
  value: TNumber,
});

const breakpointIds = [...DEFAULT_BREAKPOINT_IDS, ...LARGER_BREAKPOINT_IDS];

const FRAME_SIZE_UNIT_SETTINGS = {
  unit: 'PX',
  step: 1,
  min: 0,
} as const;

const FrameSizeDataType = TRecord(
  Object.fromEntries(
    breakpointIds.map((breakpoint) => [
      breakpoint,
      TRecord({
        width: withSystem({label: 'Width'}, FrameWidthRecord),
        height: withSystem({label: 'Height'}, FrameHeightRecord),
      }),
    ])
  )
);

const DIMENSION_MODE_NOTES: Record<
  'width' | 'height',
  Record<string, string>
> = {
  width: {
    [FRAME_DIMENSION_MODE.FIT]: 'Shrink to content width',
    [FRAME_DIMENSION_MODE.FULL]: 'Stretch to breakpoint width',
    [FRAME_DIMENSION_MODE.FIXED]: 'Use a custom width',
  } satisfies Record<FrameWidth['mode'], string>,
  height: {
    [FRAME_DIMENSION_MODE.FIT]: 'Shrink to content height',
    [FRAME_DIMENSION_MODE.FIXED]: 'Use a custom height',
  } satisfies Record<FrameHeight['mode'], string>,
};

const getUnionOptionsAtPath = (
  path: ExpressionPath,
  dimension: 'width' | 'height'
): Array<{value: string; label: string; note?: string}> => {
  const unionType = Types.__DEPRECATED__getTypeAtExpressionPath(
    FrameSizeDataType,
    path
  );
  const unionValues = unionType ? Types.getUnionValues(unionType) : null;
  if (!unionValues) return [];

  return Object.keys(unionValues).map((key) => ({
    value: key,
    label: key.charAt(0).toUpperCase() + key.slice(1),
    note: DIMENSION_MODE_NOTES[dimension][key],
  }));
};

const getDimensionMode = (
  expr: ContextualExpression,
  mediaQuery: string,
  dimension: 'width' | 'height'
): string => {
  const path = createFrameDimensionPath(mediaQuery, dimension, 'mode');
  const dimensionExpr = ContextualExpressions.getIn(expr, path);
  if (!dimensionExpr) return FRAME_DIMENSION_MODE.FIT;
  const unionValues = Expressions.getUnion(stripAll(dimensionExpr));

  return unionValues?.tag ?? FRAME_DIMENSION_MODE.FIT;
};

const getDimensionValue = (
  expr: ContextualExpression,
  mediaQuery: BreakpointID,
  dimension: 'width' | 'height'
): number => {
  const path = createFrameDimensionPath(mediaQuery, dimension, 'value');
  const dimensionExpr = ContextualExpressions.getIn(expr, path);
  if (!dimensionExpr) return FRAME_SIZE_UNIT_SETTINGS.min;
  const numberValue = Expressions.getNumber(stripAll(dimensionExpr));
  if (!numberValue) return FRAME_SIZE_UNIT_SETTINGS.min;
  if (dimension === 'height') return numberValue;
  const maxWidth = LARGER_BREAKPOINTS_CONFIG[mediaQuery]?.maxWidth;
  if (!maxWidth || numberValue <= maxWidth) return numberValue;
  return maxWidth;
};

const selectMediaQuery = (state: DesignerStores) =>
  state.ExternalStore.mediaQuery;

type FrameDimensionsProps = {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
};

const FrameDimensions: React.FC<FrameDimensionsProps> = ({expr, dispatch}) => {
  const {getNativeNode} = useDangerousHooks();
  const mediaQuery = useDesignerSelector(selectMediaQuery);

  const selectedFrameElementId = useDesignerSelector(
    selectSelectedFrameElementId
  );
  const getBaseVariantId = useDesignerSelector(selectGetBaseVariantId);
  const componentMap = useDesignerSelector(selectComponents);
  const [width, setWidth] = React.useState(0);
  const [height, setHeight] = React.useState(0);

  const widthMode = React.useMemo(
    () => getDimensionMode(expr, mediaQuery, 'width'),
    [expr, mediaQuery]
  );
  const heightMode = React.useMemo(
    () => getDimensionMode(expr, mediaQuery, 'height'),
    [expr, mediaQuery]
  );

  const isWidthFixed = widthMode === FRAME_DIMENSION_MODE.FIXED;
  const isHeightFixed = heightMode === FRAME_DIMENSION_MODE.FIXED;

  const resizeObserver = useResizeObserver((entries) => {
    const {contentRect} = entries[0] ?? {};
    if (!contentRect) return;
    if (!isWidthFixed) setWidth(contentRect.width);
    if (!isHeightFixed) setHeight(contentRect.height);
  });

  const frameSizeTransition = useFrameSizeTransition(
    selectedFrameElementId || ''
  );

  const existingWidth = getDimensionValue(expr, mediaQuery, 'width');
  const existingHeight = getDimensionValue(expr, mediaQuery, 'height');

  React.useLayoutEffect(() => {
    if (isWidthFixed) setWidth(existingWidth);
    if (isHeightFixed) setHeight(existingHeight);

    // For non-fixed width and/or height read dimensions from the DOM
    if (!isWidthFixed || !isHeightFixed) {
      const node = getNativeNode();
      if (!node) return;

      resizeObserver.observe(node);

      return () => {
        resizeObserver.unobserve(node);
      };
    }
  }, [
    isWidthFixed,
    isHeightFixed,
    existingWidth,
    existingHeight,
    mediaQuery,
    getNativeNode,
    resizeObserver,
  ]);

  if (!selectedFrameElementId) return null;
  const currentFrame = getFrameById(selectedFrameElementId, componentMap);
  if (!currentFrame) return null;
  const instanceElement = getVariantInstanceElementFromFrame(
    currentFrame,
    componentMap
  );
  if (!instanceElement) return null;

  const baseFrame = getBaseVariantFrame(componentMap, getBaseVariantId);
  if (!baseFrame) return null;

  const variant = getVariantAssociatedWithFrame(
    selectedFrameElementId,
    componentMap
  );
  if (!variant) return null;

  // Base frame on desktop (main) breakpoint should not show as overridden
  const isBaseMain =
    mediaQuery === BREAKPOINT_ID_MAIN && baseFrame.id === currentFrame.id;

  const frameOverrides = getOverrides(currentFrame);
  const hasWidthOverride =
    !isBaseMain && Boolean(frameOverrides[mediaQuery]?.width);
  const hasHeightOverride =
    !isBaseMain && Boolean(frameOverrides[mediaQuery]?.height);

  const baseVariant = getVariantAssociatedWithFrame(baseFrame.id, componentMap);
  const baseVariantName = baseVariant?.displayName ?? variant.displayName;

  const inherited = findInheritedFrom(
    currentFrame,
    variant.displayName,
    baseFrame,
    baseVariantName,
    mediaQuery
  );
  const widthInheritedFrom = inherited('width');
  const heightInheritedFrom = inherited('height');

  const widthModePath = createFrameDimensionPath(mediaQuery, 'width', 'mode');
  const widthOptions = getUnionOptionsAtPath(widthModePath, 'width');

  const heightModePath = createFrameDimensionPath(mediaQuery, 'height', 'mode');
  const heightOptions = getUnionOptionsAtPath(heightModePath, 'height');

  const {minWidth, maxWidth} = getMinMaxFrameWidthForBreakpoint(mediaQuery);
  const {minHeight, maxHeight} = getMinMaxFrameHeight();

  const createValueChangeHandler =
    (dimension: 'width' | 'height') => (value: number) => {
      const path = createFrameDimensionPath(mediaQuery, dimension);
      dispatch({
        type: FRAME_DIMENSIONS_CHANGED,
        payload: {mediaQuery, dimension, value},
        key: path,
      });
    };

  const createModeChangeHandler =
    (dimension: 'width' | 'height') => (value: string) => {
      const isFixed = value === FRAME_DIMENSION_MODE.FIXED;
      const path = createFrameDimensionPath(mediaQuery, dimension);

      dispatch({
        type: FRAME_DIMENSIONS_CHANGED,
        payload:
          dimension === 'width'
            ? {
                mediaQuery,
                dimension,
                value: isFixed ? width : value === 'fit' ? value : 'full',
              }
            : {
                mediaQuery,
                dimension,
                value: isFixed ? height : 'fit',
              },
        key: path,
      });
    };

  const handleWidthValueChange = createValueChangeHandler('width');
  const handleWidthModeChange = createModeChangeHandler('width');

  const handleHeightValueChange = createValueChangeHandler('height');
  const handleHeightModeChange = createModeChangeHandler('height');

  const createResetHandler = (dimension: 'width' | 'height') => () => {
    const path = createFrameDimensionPath(mediaQuery, dimension);

    dispatch({
      type: FRAME_DIMENSION_OVERRIDE_RESET,
      payload: {mediaQuery, dimension},
      key: path,
    });
  };

  const handleWidthReset = createResetHandler('width');
  const handleHeightReset = createResetHandler('height');

  const hasWidth = widthOptions.length > 0;
  const hasHeight = heightOptions.length > 0;

  if (!hasWidth || !hasHeight) return null;

  const transitionWidth = frameSizeTransition && frameSizeTransition.width;
  const transitionHeight = frameSizeTransition && frameSizeTransition.height;

  const widthInputValue = transitionWidth ?? width;
  const heightInputValue = transitionHeight ?? height;

  const widthModeValue = transitionWidth ? 'fixed' : widthMode;
  const heightModeValue = transitionHeight ? 'fixed' : heightMode;

  return (
    <VStack>
      <HStack>
        <FrameFieldLabel
          label="Width"
          inheritedFrom={widthInheritedFrom}
          automationId="Frame-widthLabel"
          onReset={hasWidthOverride ? handleWidthReset : undefined}
        />
        <Select
          data-automation-id="FrameSize-widthMode"
          style={{flex: 2}}
          options={widthOptions}
          value={widthModeValue ?? undefined}
          onChange={handleWidthModeChange}
          shouldPositionInline
        />
        <FrameSizeUnitInput
          automationId="FrameSize-widthValue"
          inputValue={widthInputValue}
          min={minWidth}
          max={maxWidth}
          onInputChange={handleWidthValueChange}
          dimmed={!isWidthFixed}
        />
      </HStack>
      <HStack>
        <FrameFieldLabel
          label="Height"
          inheritedFrom={heightInheritedFrom}
          automationId="Frame-heightLabel"
          onReset={hasHeightOverride ? handleHeightReset : undefined}
        />
        <Select
          data-automation-id="FrameSize-heightMode"
          style={{flex: 2}}
          options={heightOptions}
          value={heightModeValue ?? undefined}
          onChange={handleHeightModeChange}
          shouldPositionInline
        />
        <FrameSizeUnitInput
          automationId="FrameSize-heightValue"
          inputValue={heightInputValue}
          min={minHeight}
          max={maxHeight}
          onInputChange={handleHeightValueChange}
          dimmed={!isHeightFixed}
        />
      </HStack>
    </VStack>
  );
};

export const FrameSizeType = TypeImplementation.Alias({
  type: FrameSizeDataType,
  render: FrameDimensions,
});
